import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import MasterclassBanner from "@/components/MasterclassBanner";
import { Reveal, Eyebrow, CountUp, ArrowButton, ChipFlow, EASE } from "@/components/bits";
import ProjectCard from "@/components/ProjectCard";
import { CAPABILITIES, STATS, EVOLUTION, HERO_FLOW, PROJECTS, WHY_PARTNER } from "@/data/content";

function HeroVisual() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
      className="relative"
      data-testid="hero-visual"
    >
      <div className="bg-white border border-tnn-ink/8 rounded-3xl shadow-[0_24px_60px_rgba(40,0,40,0.08)] p-7 animate-floaty">
        <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-tnn-purple mb-5">From nutrient to system</p>
        <div>
          {HERO_FLOW.map((step, i) => (
            <motion.div
              key={step}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 + i * 0.1, duration: 0.45, ease: EASE }}
              className="flex items-center gap-4"
            >
              <div className="flex flex-col items-center w-3">
                <span className={`w-2.5 h-2.5 rounded-full ${i === HERO_FLOW.length - 1 ? "bg-tnn-purple" : "bg-tnn-lilac"}`} />
                {i < HERO_FLOW.length - 1 && <span className="w-px h-6 bg-tnn-purple/15" />}
              </div>
              <div className="flex items-baseline gap-3 py-0.5">
                <span className="text-[10px] font-bold text-tnn-purple/40">0{i + 1}</span>
                <span className="font-display font-semibold text-tnn-ink">{step}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      <span aria-hidden="true" className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-tnn-soft -z-10" />
      <span aria-hidden="true" className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full bg-tnn-mist -z-10" />
    </motion.div>
  );
}

function Hero() {
  return (
    <section data-testid="home-hero" className="bg-tnn-warm overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 pt-36 lg:pt-44 pb-16 lg:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <Reveal y={16}>
              <Eyebrow>The Nutrition Network Africa</Eyebrow>
            </Reveal>
            <Reveal delay={0.08} y={20}>
              <h1 className="mt-5 font-display text-4xl sm:text-5xl font-bold tracking-tight text-tnn-ink leading-[1.12]" data-testid="hero-headline">
                Building better <span className="text-tnn-purple">nutrition systems</span> through science, technology and innovation.
              </h1>
            </Reveal>
            <Reveal delay={0.16} className="mt-6 max-w-lg space-y-4">
              <p className="text-base sm:text-lg text-tnn-muted leading-relaxed">
                The Nutrition Network Africa (TNN Africa) is a nutrition and health innovation organisation working at the
                intersection of clinical nutrition, digital health, product development and healthcare transformation.
              </p>
              <p className="text-sm sm:text-base text-tnn-muted/80 leading-relaxed">
                We work with healthcare organisations, founders, technology companies and institutions to turn complex
                nutrition and health challenges into practical products, programmes and scalable solutions.
              </p>
            </Reveal>
            <Reveal delay={0.24} className="mt-8 flex flex-wrap gap-3">
              <ArrowButton to="/contact" testid="hero-cta-partner">Partner With Us</ArrowButton>
              <ArrowButton to="/our-work" variant="outlineDark" testid="hero-cta-work">Explore Our Work</ArrowButton>
            </Reveal>
          </div>
          <HeroVisual />
        </div>
      </div>
    </section>
  );
}

function WhatWeDo() {
  return (
    <section data-testid="home-what-we-do" className="bg-white py-16 lg:py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <Reveal>
              <Eyebrow>What We Do</Eyebrow>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-tnn-ink leading-[1.15]">
                We don't just talk about nutrition. We build what nutrition needs next.
              </h2>
            </Reveal>
            <Reveal delay={0.12} className="mt-6 space-y-4 text-tnn-muted leading-relaxed text-[15px]">
              <p>Nutrition challenges are rarely solved by information alone.</p>
              <p>
                They require better products, stronger care pathways, accessible services and technology designed around the
                realities of the people using them.
              </p>
              <p>
                TNN Africa brings together clinical expertise, product thinking, technology and entrepreneurship to build
                and support those solutions.
              </p>
            </Reveal>
            <Reveal delay={0.2} className="mt-8">
              <ArrowButton to="/about" variant="ghost" testid="what-we-do-cta">Learn About TNN Africa</ArrowButton>
            </Reveal>
          </div>
          <div className="space-y-4">
            {CAPABILITIES.map((cap, i) => (
              <Reveal key={cap.title} delay={i * 0.06}>
                <div
                  data-testid={`capability-card-${i}`}
                  className="group bg-tnn-soft hover:bg-tnn-purple rounded-2xl p-5 sm:p-6 transition-colors duration-300"
                >
                  <h3 className="font-display text-lg font-semibold text-tnn-ink group-hover:text-white transition-colors duration-300">
                    {cap.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-tnn-muted group-hover:text-white/75 transition-colors duration-300">
                    {cap.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Impact() {
  return (
    <section data-testid="home-impact" className="bg-tnn-soft py-16 lg:py-24">
      <div className="max-w-6xl mx-auto px-6">
        <Reveal>
          <Eyebrow>Selected Impact</Eyebrow>
        </Reveal>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div data-testid={`impact-stat-${i}`}>
                <p className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-tnn-purple">
                  <CountUp to={s.value} suffix={s.suffix} />
                </p>
                <p className="mt-3 text-sm text-tnn-muted leading-relaxed">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.25}>
          <p className="mt-10 text-xs text-tnn-muted/80 italic max-w-xl">
            These figures combine results from TNN Africa-led work and ventures we have supported alongside partners.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Evolution() {
  return (
    <section data-testid="home-evolution" className="bg-white py-16 lg:py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="lg:sticky lg:top-28">
              <Reveal>
                <Eyebrow>Our Evolution</Eyebrow>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-tnn-ink leading-[1.15]">
                  From clinical nutrition to healthcare innovation.
                </h2>
                <p className="mt-5 text-[15px] text-tnn-muted leading-relaxed">
                  Our work has evolved alongside the problems we are trying to solve. What began with clinical nutrition
                  has grown into a broader practice of building products, ventures and systems that improve how people
                  experience healthcare.
                </p>
                <div className="mt-7">
                  <ArrowButton to="/about" variant="ghost" testid="evolution-cta">Our Story</ArrowButton>
                </div>
              </Reveal>
            </div>
          </div>
          <div className="space-y-3">
            {EVOLUTION.map((stage, i) => (
              <Reveal key={stage.title} delay={i * 0.05}>
                <div
                  data-testid={`evolution-stage-${i}`}
                  className="flex items-start gap-4 bg-tnn-warm border border-tnn-ink/6 rounded-2xl p-5 transition-colors duration-300 hover:border-tnn-purple/30"
                >
                  <span className="w-8 h-8 rounded-full bg-tnn-soft text-tnn-purple text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-display font-semibold text-tnn-ink">{stage.title}</h3>
                    <p className="mt-1 text-sm text-tnn-muted leading-relaxed">{stage.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturedWork() {
  const featured = ["platemind", "onconourish", "lana-clinical", "sito-health", "healthy-cooking-masterclass"]
    .map((slug) => PROJECTS.find((p) => p.slug === slug))
    .filter(Boolean);
  return (
    <section data-testid="home-featured-work" className="bg-tnn-warm py-16 lg:py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <Reveal>
            <Eyebrow>Featured Work</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-tnn-ink">What we've built</h2>
            <p className="mt-4 text-[15px] text-tnn-muted max-w-xl leading-relaxed">
              From digital health platforms to nutrition technology, oncology support and AI-enabled healthcare, our work
              spans multiple parts of the health ecosystem.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <ArrowButton to="/our-work" variant="outlineDark" testid="featured-work-cta">Explore Our Work</ArrowButton>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featured.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.06}>
              <div data-testid={`featured-project-${p.slug}`}>
                <ProjectCard project={p} />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyPartner() {
  return (
    <section data-testid="home-why-partner" className="bg-white py-16 lg:py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Eyebrow>Why Partner With Us</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-tnn-ink leading-[1.15]">
              Clinical expertise. Product thinking. Technology.
            </h2>
            <p className="mt-5 text-[15px] text-tnn-muted leading-relaxed">
              We operate between clinical practice and technology. That allows us to understand healthcare problems from
              the inside while also having the product and innovation capabilities required to build solutions around them.
            </p>
            <div className="mt-7">
              <ArrowButton to="/partnerships" variant="outlineDark" testid="why-partner-cta">Explore Partnerships</ArrowButton>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="bg-tnn-warm border border-tnn-ink/6 rounded-3xl p-8">
              <ChipFlow items={WHY_PARTNER} />
              <div className="my-6 flex items-center gap-4">
                <span className="h-px flex-1 bg-tnn-ink/10" />
                <span className="font-display font-bold text-tnn-purple">=</span>
                <span className="h-px flex-1 bg-tnn-ink/10" />
              </div>
              <p className="font-display text-2xl font-bold tracking-tight text-tnn-ink" data-testid="why-partner-result">
                Practical Healthcare <span className="text-tnn-purple">Innovation</span>
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FoundersTeaser() {
  return (
    <section data-testid="home-founders-teaser" className="bg-tnn-warm py-16 lg:py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal className="order-2 lg:order-1">
            <Link to="/founders" data-testid="founders-teaser-link" className="group block">
              <div className="bg-white border border-tnn-ink/8 rounded-3xl p-4 shadow-[0_16px_44px_rgba(40,0,40,0.07)] transition-shadow duration-300 group-hover:shadow-[0_24px_60px_rgba(40,0,40,0.14)]">
                <img
                  src="/assets/founders.webp"
                  alt="Tito Ipinmoye and Evi Uwede, founders of TNN Africa"
                  data-testid="founders-teaser-image"
                  loading="lazy"
                  className="w-full rounded-2xl object-cover aspect-[5/4]"
                />
                <div className="flex items-center justify-between px-2 pt-4 pb-1">
                  <p className="font-display font-semibold text-sm text-tnn-ink">Tito Ipinmoye & Evi Uwede</p>
                  <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-tnn-purple transition-colors duration-200 group-hover:text-tnn-deep">
                    Meet the Founders
                  </p>
                </div>
              </div>
            </Link>
          </Reveal>
          <div className="order-1 lg:order-2">
            <Reveal>
              <Eyebrow>The Founders</Eyebrow>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-tnn-ink leading-[1.15]">
                Built by people who understand healthcare from the inside.
              </h2>
              <p className="mt-5 text-[15px] text-tnn-muted leading-relaxed">
                TNN Africa was founded from a simple belief: the future of nutrition requires both clinical expertise and
                the ability to build.
              </p>
            </Reveal>
            <Reveal delay={0.12} className="mt-8 flex flex-wrap gap-8">
              <div data-testid="founder-teaser-tito">
                <p className="font-display font-semibold text-lg text-tnn-ink">Tito Ipinmoye</p>
                <p className="mt-0.5 text-xs font-bold tracking-[0.18em] text-tnn-purple uppercase">Founder</p>
              </div>
              <div data-testid="founder-teaser-evi">
                <p className="font-display font-semibold text-lg text-tnn-ink">Evi Uwede</p>
                <p className="mt-0.5 text-xs font-bold tracking-[0.18em] text-tnn-purple uppercase">Co-Founder</p>
              </div>
            </Reveal>
            <Reveal delay={0.2} className="mt-8">
              <ArrowButton to="/founders" variant="ghost" testid="founders-teaser-cta">Meet the Founders</ArrowButton>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section data-testid="home-final-cta" className="bg-tnn-warm pb-16 lg:pb-24">
      <div className="max-w-6xl mx-auto px-6">
        <Reveal>
          <div className="bg-tnn-deep rounded-[2rem] px-8 py-14 lg:p-20">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.15] max-w-2xl">
              Have a nutrition or healthcare problem <span className="text-tnn-lilac">worth solving?</span>
            </h2>
            <p className="mt-5 text-white/65 text-base sm:text-lg">Let's turn it into something people can use.</p>
            <div className="mt-9">
              <ArrowButton to="/contact" variant="white" testid="final-cta-partner">Partner With TNN Africa</ArrowButton>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <WhatWeDo />
      <Impact />
      <Evolution />
      <FeaturedWork />
      <WhyPartner />
      <FoundersTeaser />
      <FinalCTA />
      <MasterclassBanner />
    </main>
  );
}
